import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { orgSchema } from "../../common/orgSchema";

const orgUsersSchema = z.object({
  id: z.number(),
  agencies: z.array(z.string()),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  is_active: z.boolean(),
  is_staff: z.boolean(),
  last_login: z.string().datetime().nullable(),
  username: z.string(),
  login_method: z.string(),
  roles: z.array(
    z.object({
      membership_id: z.number(),
      membership_status: z.string(),
      role_id: z.number(),
      role_name: z.string(),
    }),
  ),
});

const userInvitationDetailsSchema = z.object({
  id: z.number(),
  activated: z.boolean(),
  expired: z.boolean(),
  invited_email: z.string(),
  invited_on: z.string().date(),
  role: z.string(),
  is_trellis_staff: z.boolean().optional(),
});

const updateUserRoleRequestSchema = z.object({
  user_id: z.number(),
  rbac_role_id: z.number(),
  status: z.string(),
});

const bulkCreateMembershipSchema = z.object({
  user_id: z.number(),
  org_id: z.number(),
  role_name: z.string(),
});

const bulkUpdateMembershipSchema = z.object({
  membership_id: z.number(),
  role_name: z.string(),
});

const bulkDeleteMembershipSchema = z.object({
  membership_id: z.number(),
});

export const bulkMutateUserRolesRequestSchema = z.object({
  creates: z.array(bulkCreateMembershipSchema).default([]),
  updates: z.array(bulkUpdateMembershipSchema).default([]),
  deletes: z.array(bulkDeleteMembershipSchema).default([]),
  upserts: z.array(bulkCreateMembershipSchema).default([]),
});

const userRoleOptionSchema = z.object({
  id: z.number(),
  is_custom: z.boolean(),
  marketplace: z.string().nullable(),
  org_id: z.number(),
  permissions: z.object({ perm: z.array(z.string()) }),
  role_name: z.string(),
});

const managedUserRolesSchema = z.object({
  orgs: z.array(orgSchema),
  users: z.array(
    z.object({
      id: z.number(),
      username: z.string(),
      email: z.string().nullable(),
      first_name: z.string().nullable(),
      last_name: z.string().nullable(),
      last_login: z.string().datetime().nullable(),
      memberships: z.array(
        z.object({
          org_id: z.number(),
          membership_id: z.number(),
          membership_status: z.string(),
          role_id: z.number(),
          role_name: z.string(),
        }),
      ),
    }),
  ),
});

const createUserInvitationRequestSchema = z.object({
  invite_email: z.string().email(),
  login_type: z.string(),
  user_permissions: z.object({
    user_roles: z.record(z.string(), z.string()),
    org_ids: z.array(z.number()).optional(),
    dashboard: z.boolean(),
    is_trellis_staff: z.boolean().optional(),
  }),
});

const userRoleMembershipSchema = z.object({
  id: z.number(),
  org: orgSchema,
  role_id: z.number(),
  role_name: z.string(),
});

export const userManagementApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "updateUserActiveStatus",
    method: "patch",
    path: "/user_status/",
    status: 200,
    parameters: [{ name: "body", type: "Body", schema: z.object({ user_id: z.number(), is_active: z.boolean() }) }],
    response: z.object({}),
  })
    .addEndpoint({
      alias: "getOrgUsers",
      method: "get",
      path: "/org_users/",
      parameters: [
        {
          name: "include_staff",
          type: "Query",
          schema: z.boolean().optional(),
        },
        {
          name: "agency_id",
          type: "Query",
          schema: z.number().optional(),
        },
      ],
      status: 200,
      response: z.array(orgUsersSchema),
    })
    .addEndpoint({
      alias: "getUserOrgPermissions",
      method: "get",
      path: "/user_org_permissions/",
      parameters: [
        {
          name: "user_id",
          type: "Query",
          schema: z.number(),
        },
      ],
      status: 200,
      response: z.object({
        memberships: z.array(userRoleMembershipSchema),
        user_profile: z.object({
          id: z.number(),
          user_id: z.number(),
          first_name: z.string(),
          last_name: z.string(),
          email: z.string(),
          date_joined: z.string().datetime(),
          last_login: z.string().datetime(),
        }),
      }),
    })
    .addEndpoint({
      alias: "getUserInvitations",
      method: "get",
      path: "/invite/",
      parameters: [],
      status: 200,
      response: z.object({ invites: z.array(userInvitationDetailsSchema), roles: z.array(z.string()) }),
    })
    .addEndpoint({
      alias: "getUserRoleOptions",
      method: "get",
      path: "/users_membership_roles/:org_id/",
      parameters: [
        {
          name: "org_id",
          type: "Path",
          schema: z.string(),
          description: "Organization ID",
        },
      ],
      status: 200,
      response: z.object({ roles: z.array(userRoleOptionSchema) }),
    })
    .addEndpoint({
      alias: "getManagedUserOrgRoles",
      method: "get",
      path: "/user_roles_matrix/",
      parameters: [
        {
          name: "agency_id",
          type: "Query",
          schema: z.string().optional(),
          description: "Agency ID",
        },
      ],
      status: 200,
      response: managedUserRolesSchema,
    })
    .addEndpoint({
      alias: "createUserInvitation",
      method: "post",
      path: "/invite/",
      parameters: [{ name: "body", type: "Body", schema: createUserInvitationRequestSchema }],
      status: 201,
      response: z.string(),
    })
    .addEndpoint({
      alias: "resendUserInvitation",
      method: "post",
      path: "/invite/resend/",
      parameters: [{ name: "body", type: "Body", schema: z.object({ invite_email: z.string().email() }) }],
      status: 200,
      response: z.string(),
    })
    .addEndpoint({
      alias: "deleteUserInvitation",
      method: "post",
      path: "/invite/delete/",
      parameters: [{ name: "body", type: "Body", schema: z.object({ invite_email: z.string().email() }) }],
      status: 200,
      response: z.string(),
    })
    .addEndpoint({
      alias: "updateUserRole",
      method: "put",
      path: "/users_memberships/:org_id/",
      parameters: [{ name: "body", type: "Body", schema: updateUserRoleRequestSchema }],
      status: 200,
      response: z.string(),
    })
    .addEndpoint({
      alias: "bulkMutateUserRoles",
      method: "post",
      path: "/rbac/memberships/bulk_mutate/",
      parameters: [{ name: "body", type: "Body", schema: bulkMutateUserRolesRequestSchema }],
      status: 200,
      response: z.object({
        created: z.number(),
        updated: z.number(),
        deleted: z.number(),
      }),
    })
    .build(),
);

export type OrgUser = z.infer<typeof orgUsersSchema>;
export type UserInvitationDetails = z.infer<typeof userInvitationDetailsSchema>;
export type UserInvitationUpsertRequest = z.infer<typeof createUserInvitationRequestSchema>;
export type UserOrgRoleMembership = z.infer<typeof userRoleMembershipSchema>;
